import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaPlay, FaBookmark, FaCheck, FaTv } from 'react-icons/fa';
import { MovieContext } from '../context/Movies.context';

const FALLBACK_POSTER =
  'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=300&q=80';

const posterUrlFor = (path) =>
  path ? (path.startsWith('http') ? path : `https://image.tmdb.org/t/p/w300${path}`) : FALLBACK_POSTER;

// Shared dense row for the Movies/Series "list view" of the infinite feed.
// Movies show an in-app details link; TV shows link out to TMDB because the app
// has no per-episode catalog page for arbitrary shows.
const CatalogListRow = ({ item, mediaType = 'movie', detailPath }) => {
  const { openTrailer, openWatch, toggleMyList, isInMyList } = useContext(MovieContext);
  const isSeries = mediaType === 'tv';
  const isSaved = isInMyList ? isInMyList(item.id) : false;
  const title = item.title || item.original_title || 'Untitled';

  return (
    <div className="bg-dark-800/80 rounded-2xl border border-white/10 p-4 flex flex-col sm:flex-row gap-5 hover:border-accent-gold/40 transition-all shadow-md group">
      <Link
        to={detailPath}
        className="w-full sm:w-28 h-40 flex-shrink-0 rounded-xl overflow-hidden bg-dark-700"
      >
        <img
          src={posterUrlFor(item.poster_path)}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </Link>

      <div className="flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-2">
            <Link to={detailPath}>
              <h3 className="text-lg font-bold text-white group-hover:text-accent-gold transition-colors">
                {title}
              </h3>
            </Link>
            {item.vote_average ? (
              <span className="flex items-center gap-1 text-xs font-bold text-accent-gold bg-black/60 px-2.5 py-1 rounded-lg border border-white/10">
                <FaStar className="w-3 h-3" aria-hidden="true" />
                {Number(item.vote_average).toFixed(1)}
              </span>
            ) : null}
          </div>

          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
            {item.release_date ? new Date(item.release_date).getFullYear() : 'N/A'}
            <span aria-hidden="true">•</span>
            {isSeries ? (
              <span className="inline-flex items-center gap-1">
                <FaTv className="w-3 h-3" aria-hidden="true" /> TV Series
              </span>
            ) : (
              <span>4K Ultra HD</span>
            )}
          </p>

          <p className="text-gray-300 text-xs sm:text-sm line-clamp-2 mt-2 leading-relaxed">
            {item.overview || 'No synopsis available for this title.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-3 mt-2 border-t border-white/5">
          <button
            type="button"
            onClick={() => openWatch && openWatch(item)}
            className="inline-flex items-center gap-1.5 bg-accent-gold hover:bg-accent-goldHover text-dark-900 text-xs font-bold px-4 py-2 rounded-lg transition-all"
          >
            <FaPlay className="w-2.5 h-2.5" aria-hidden="true" />
            <span>Watch Now</span>
          </button>

          <button
            type="button"
            onClick={() => openTrailer && openTrailer(item)}
            className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition-all border border-white/10"
          >
            <FaPlay className="w-2.5 h-2.5 text-accent-gold" aria-hidden="true" />
            <span>Trailer</span>
          </button>

          <Link
            to={detailPath}
            className="inline-flex items-center text-xs font-semibold text-gray-300 hover:text-white px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
          >
            {isSeries ? 'Episodes & Details' : 'Details & Tickets'}
          </Link>

          <button
            type="button"
            onClick={() => toggleMyList && toggleMyList(item)}
            aria-label={isSaved ? 'Remove from watchlist' : 'Add to watchlist'}
            className={`p-2 rounded-lg border transition-colors ${
              isSaved
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                : 'bg-white/5 hover:bg-white/10 text-gray-300 border-white/10'
            }`}
          >
            {isSaved ? <FaCheck className="w-3 h-3" /> : <FaBookmark className="w-3 h-3" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatalogListRow;
